import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';

interface TransferAgent {
  _id: string;
  name: string;
  address: {
    street: string;
    city: string;
    state: string;
    zipCode: string;
  };
  phone: string;
  website: string;
}

interface TransferAgentAutocompleteProps {
  value: string;
  onChange: (agent: TransferAgent | null, name: string) => void;
  disabled?: boolean;
  required?: boolean;
}

const TransferAgentAutocomplete: React.FC<TransferAgentAutocompleteProps> = ({
  value,
  onChange,
  disabled = false,
  required = false
}) => {
  const [searchTerm, setSearchTerm] = useState(value || '');
  const [suggestions, setSuggestions] = useState<TransferAgent[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Handle clicks outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Search for transfer agents
  useEffect(() => {
    const searchAgents = async () => {
      if (searchTerm.length < 2) {
        setSuggestions([]);
        return;
      }

      setLoading(true);
      try {
        const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
        const response = await axios.get(`${baseURL}/api/transfer-agents/search`, {
          params: { query: searchTerm }
        });
        
        if (response.data.success) {
          setSuggestions(response.data.data);
        }
      } catch (error) {
        console.error('Error searching transfer agents:', error);
      } finally {
        setLoading(false);
      }
    };

    const debounceTimer = setTimeout(searchAgents, 300);
    return () => clearTimeout(debounceTimer);
  }, [searchTerm]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    setShowSuggestions(true);
    
    // If the value is cleared, notify parent
    if (!value) {
      onChange(null, '');
    }
  };

  const handleSelectAgent = (agent: TransferAgent) => {
    setSearchTerm(agent.name);
    setShowSuggestions(false);
    onChange(agent, agent.name);
  };

  const formatAddress = (agent: TransferAgent) => {
    const { street, city, state, zipCode } = agent.address;
    return `${street}, ${city}, ${state} ${zipCode}`;
  };

  return (
    <div ref={wrapperRef} className="relative">
      <div className="relative">
        <input
          type="text"
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={() => setShowSuggestions(true)}
          placeholder="Start typing to search transfer agents..."
          className="w-full px-4 py-3 pr-10 border border-secondary-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          disabled={disabled}
          required={required}
        />
        <div className="absolute inset-y-0 right-0 flex items-center pr-3">
          {loading ? (
            <div className="animate-spin h-5 w-5 border-2 border-primary-500 border-t-transparent rounded-full" />
          ) : (
            <MagnifyingGlassIcon className="h-5 w-5 text-secondary-400" />
          )}
        </div>
      </div>

      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute z-10 w-full mt-1 bg-white rounded-xl shadow-lg border border-secondary-200 max-h-60 overflow-auto">
          {suggestions.map((agent) => (
            <button
              key={agent._id}
              type="button"
              onClick={() => handleSelectAgent(agent)}
              className="w-full px-4 py-3 text-left hover:bg-primary-50 focus:bg-primary-50 focus:outline-none transition-colors"
            >
              <div className="font-medium text-secondary-900">{agent.name}</div>
              <div className="text-sm text-secondary-600 mt-1">
                {formatAddress(agent)}
              </div>
              {agent.phone && (
                <div className="text-sm text-secondary-500">
                  Phone: {agent.phone}
                </div>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default TransferAgentAutocomplete; 